import { useState } from "react";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";

const siteUrl = typeof window !== "undefined" ? window.location.origin + __BASE_PATH__ : "";

export default function AboutPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);

    const honeypot = (fd.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setFormStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "" });
      return;
    }

    if ((fd.get("message") as string).length > 500) {
      setFormError("留言内容不能超过500个字符。");
      setFormStatus("error");
      return;
    }

    try {
      const params = new URLSearchParams();
      fd.forEach((value, key) => {
        if (key !== "phone_alt") {
          params.append(key, value as string);
        }
      });

      const res = await fetch("https://readdy.ai/api/form/d9qr794nlsngrm4lm4hg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      const code = (parsed?.code as string) || "";
      const serverMsg = (parsed?.meta?.message as string) || (parsed?.message as string) || "";

      if (res.ok && code === "OK") {
        setFormStatus("success");
        setFormError("");
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        setFormStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请稍后重试。");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title="关于我们 - K-Stream 解析 | 韩国女主播深度解析平台"
        description="K-Stream 解析是一个专注于韩国女主播直播文化的深度内容平台。我们致力于为中文用户提供最专业、最全面的韩国女主播直播风格解析、平台分析、互动技巧与变现模式解读。"
        keywords="关于我们,K-Stream 解析,韩国女主播,品牌介绍,联系我们"
        canonicalUrl={siteUrl + "/about"}
        breadcrumbs={[
          { name: "首页", url: siteUrl + "/" },
          { name: "关于我们", url: siteUrl + "/about" },
        ]}
      />

      {/* Header */}
      <section className="pt-28 pb-10 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb />
          <div className="text-center max-w-2xl mx-auto">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              关于我们
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              了解 K-Stream 解析的品牌故事、核心团队与使命愿景
            </p>
          </div>
        </div>
      </section>

      {/* Brand Introduction */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-full h-72 md:h-96 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20multiple%20monitors%20displaying%20streaming%20content%20analytics%20team%20collaboration%20around%20large%20screen%20showing%20data%20visualization%20warm%20ambient%20lighting%20clean%20minimal%20design%20professional%20yet%20approachable%20atmosphere%20editorial%20photography%20style&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                  alt="K-Stream 解析 品牌形象"
                  className="w-full h-full object-cover"
                  title="K-Stream 解析 - 韩国女主播深度解析平台"
                />
              </div>
            </div>
            <div>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-600 mb-4">
                品牌故事
              </span>
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-5">
                专注韩国女主播直播文化
                <span className="block text-primary-500">深度解析平台</span>
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  K-Stream 解析成立于2024年，是国内最早专注于<strong>韩国女主播</strong>直播文化研究的深度内容平台之一。我们的创始团队由韩国文化研究者、资深媒体人和数据分析师组成，对韩国直播产业有着深入的观察与独到的见解。
                </p>
                <p>
                  我们致力于为中文用户提供最专业、最全面的<strong>韩国女主播</strong>相关深度内容，涵盖直播风格解析、平台选择策略、互动技巧揭秘、粉丝运营方法论、变现模式拆解以及主播推荐等多个维度。
                </p>
                <p>
                  每一篇文章都经过严格的资料核实和多轮编辑审核，确保内容的专业性和可读性。我们相信，优质的深度内容能够帮助读者更好地理解<strong>韩国女主播</strong>直播文化的魅力与商业逻辑。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              我们的使命与价值
            </h2>
            <p className="text-foreground-600 text-sm max-w-xl mx-auto">
              以专业深度内容为桥梁，连接中文读者与韩国直播文化
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: "ri-search-eye-line",
                title: "深度研究",
                desc: "我们坚持对每一个话题进行深入研究和多维度分析，拒绝浅尝辄止的信息搬运，为读者提供有深度、有见解的原创内容。",
              },
              {
                icon: "ri-shield-check-line",
                title: "专业可靠",
                desc: "所有内容均经过严格的资料核实和编辑审核流程，确保信息的准确性和观点的客观性，建立读者信赖的专业品牌形象。",
              },
              {
                icon: "ri-global-line",
                title: "文化桥梁",
                desc: "我们致力于跨越语言和文化壁垒，将韩国直播文化的精髓以中文读者易于理解的方式呈现，促进中韩文化交流。",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/70 text-center"
              >
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-5">
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reputation */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              行业声誉
            </h2>
            <p className="text-foreground-600 text-sm max-w-xl mx-auto">
              来自读者与行业的认可，是我们持续前进的动力
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { num: "50万+", label: "月均阅读量" },
              { num: "98%", label: "读者满意度" },
              { num: "14+", label: "深度原创文章" },
              { num: "30+", label: "合作媒体平台" },
            ].map((item) => (
              <div key={item.label} className="p-6 rounded-lg bg-background-100">
                <p className="font-heading text-3xl md:text-4xl font-bold text-primary-500 mb-2">
                  {item.num}
                </p>
                <p className="text-sm text-foreground-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
              <i className="ri-mail-line text-2xl"></i>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              联系我们
            </h2>
            <p className="text-foreground-600 text-sm">
              有任何问题、建议或合作意向，欢迎随时联系我们
            </p>
          </div>

          {formStatus === "success" ? (
            <div className="text-center py-12 bg-background-50 rounded-lg border border-background-200/70">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                <i className="ri-check-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                提交成功！
              </h3>
              <p className="text-sm text-foreground-600 mb-6">
                感谢你的留言，我们会尽快回复你。
              </p>
              <button
                onClick={() => setFormStatus("idle")}
                className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                继续留言
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-red-400">*</span>
                  <span className="text-foreground-400 font-normal ml-1">（限500字）</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入你想说的话..."
                />
                <p className="text-xs text-foreground-400 mt-1 text-right">
                  {formData.message.length}/500
                </p>
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 pointer-events-none"
              />
              <button
                type="submit"
                className="w-full py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                发送留言
              </button>
              {formStatus === "error" && formError && (
                <p className="text-red-400 text-sm text-center">{formError}</p>
              )}
            </form>
          )}
        </div>
      </section>
    </div>
  );
}